import { useEffect, useRef } from "react";
import Card from "../Card";
import FormField from "../FormField";
import SectionHeader from "../SectionHeader";
import SourceBanner from "../SourceBanner";
import { ProgressReportPrefill } from "../../../../../hooks/useConstructionProgressReport";

interface Props {
  prefill?: ProgressReportPrefill;
  invoiceAmount?: string;
  onInvoiceAmountChange?: (val: string) => void;
  invoiceDate?: string;
  onInvoiceDateChange?: (val: string) => void;
  invoiceNumber?: string;
  onInvoiceNumberChange?: (val: string) => void;
  reportDate?: string;
  onReportDateChange?: (val: string) => void;
  servicesRequested?: string;
  onServicesRequestedChange?: (val: string) => void;
  inspectorName?: string;
  submitted?: boolean;
}

const fmt = (amount?: number, symbol = "$"): string =>
  amount !== undefined
    ? `${symbol}${amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
    : "—";

const Step1ProjectDrawInfo = ({
  prefill,
  invoiceAmount = "",
  onInvoiceAmountChange,
  invoiceDate = "",
  onInvoiceDateChange,
  invoiceNumber = "",
  onInvoiceNumberChange,
  reportDate = "",
  onReportDateChange,
  servicesRequested = "",
  onServicesRequestedChange,
  inspectorName = "",
  submitted = false,
}: Props) => {
  const rootRef = useRef<HTMLDivElement>(null);

  const invoiceNumberErr =
    submitted && !invoiceNumber.trim() ? "Invoice Number is required" : "";
  const invoiceDateErr =
    submitted && !invoiceDate.trim() ? "Invoice Date is required" : "";
  const reportDateErr =
    submitted && !reportDate.trim() ? "Report Date is required" : "";
  const invoiceAmountErr =
    submitted && !invoiceAmount.trim() ? "Invoice Amount is required" : "";
  const servicesRequestedErr =
    submitted && !servicesRequested.trim()
      ? "Services Requested is required"
      : "";

  useEffect(() => {
    if (
      submitted &&
      (invoiceNumberErr ||
        invoiceDateErr ||
        reportDateErr ||
        invoiceAmountErr ||
        servicesRequestedErr)
    ) {
      rootRef.current
        ?.querySelector<HTMLElement>(".field-error")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted]);

  const sym = prefill?.draw?.g702?.originalContractSum?.currency?.symbol ?? "$";
  const g702 = prefill?.draw?.g702;
  const project = prefill?.project;
  const stakeholders = prefill?.stakeholders;
  const inspection = prefill?.inspection;
  const inspectionDate =
    inspection?.inspectionDate ?? prefill?.draw?.inspectionDate ?? "";

  const lenderName = stakeholders?.lender?.companyName || "";
  const lendingOfficer = stakeholders?.lendingOfficer
    ? `${stakeholders.lendingOfficer.firstName ?? ""} ${stakeholders.lendingOfficer.lastName ?? ""}`.trim()
    : "";
  const ownerName = stakeholders?.owner ? stakeholders.owner.companyName : "";
  const gcName = stakeholders?.gc
    ? stakeholders.gc.companyName ||
      `${stakeholders.gc.firstName ?? ""} ${stakeholders.gc.lastName ?? ""}`.trim()
    : "";

  const leftSummary: [string, string, string][] = [
    ["1", "Original contract sum", fmt(g702?.originalContractSum?.amount, sym)],
    // ["1a", "SOV update / final contract", fmt(g702?.sovUpdate?.amount, sym)],
    [
      "2",
      "Net change by change orders",
      fmt(g702?.netChangeByChangeOrders?.amount, sym),
    ],
    ["3", "Contract sum to date", fmt(g702?.contractSumToDate?.amount, sym)],
    [
      "4",
      "Total completed and stored",
      fmt(g702?.totalCompletedAndStored?.amount, sym),
    ],
  ];

  const rightSummary: [string, string, string, string?, string?][] = [
    [
      "5a",
      `${parseFloat(String(g702?.retainageRate ?? 0))}% of completed work`,
      fmt(g702?.completedWorkRetainage?.amount, sym),
    ],
    [
      "5b",
      `${parseFloat(String(g702?.retainageRate ?? 0))}%  of stored material`,
      fmt(g702?.storedAmountRetainage?.amount, sym),
    ],
    ["5", "Total retainage", fmt(g702?.totalRetainage?.amount, sym)],
    [
      "6",
      "Total earned less retainage",
      fmt(g702?.totalEarnedLessRetainage?.amount, sym),
      "blue",
    ],
    [
      "7",
      "Less previous certificates",
      fmt(g702?.lessPreviousCertificates?.amount, sym),
    ],
    [
      "8",
      "Current payment due",
      fmt(g702?.currentPaymentDue?.amount, sym),
      "green",
      "total",
    ],
    [
      "9",
      "Balance to finish incl. retainage",
      fmt(g702?.balanceToFinish?.amount, sym),
    ],
  ];

  const drawNum = prefill?.draw?.drawNumber;
  const reportNum = prefill?.nextReportNumber;
  const invoiceNum = prefill?.invoiceNumber;
  return (
    <div className="craScreen" ref={rootRef}>
      <SectionHeader
        title="Project and Draw Information"
        subtitle="Auto-populated from the platform. Edit any fields if needed before proceeding."
      />
      <SourceBanner
        icon="✅"
        title="Field Inspection Submitted"
        text={
          inspection
            ? `Inspector ${inspection.inspectorName} submitted FI Report #${inspection.inspectionNumber} on ${inspection.inspectionDate}. Data is pre-filled from draw request and FI submission.`
            : "Data is pre-filled from draw request and FI submission."
        }
      />

      <Card
        title="Project Overview"
        badge={{ label: "Auto-filled", variant: "auto" }}
      >
        <div className="craFieldRow cols2">
          <FormField label="Bank / Lender" auto value={lenderName} />
          <FormField label="Lending Officer(s)" auto value={lendingOfficer} />
        </div>
        <div className="craFieldRow cols2">
          <FormField label="Project Name" auto value={project?.name ?? ""} />
          <FormField
            label="Project Location"
            auto
            value={project?.address ?? ""}
          />
        </div>
        <div className="craFieldRow cols2">
          <FormField
            label="Description"
            auto
            value={project?.description ?? ""}
          />
          <FormField label="Owner" auto value={ownerName} />
        </div>
        <div className="craFieldRow cols2">
          <FormField label="General Contractor" auto value={gcName} />
          <FormField
            label="Services Requested"
            required
            value={servicesRequested}
            onChange={onServicesRequestedChange}
            error={servicesRequestedErr}
          />
        </div>
      </Card>

      <Card
        title="Report and Inspection Details"
        badge={{ label: "Editable", variant: "editable" }}
      >
        <div className="craFieldRow cols4">
          <FormField
            label="Report Number"
            required
            value={reportNum !== undefined ? String(reportNum) : ""}
          />
          <FormField label="Inspection Date" auto value={inspectionDate} />
          <FormField
            label="Report Date"
            required
            type="date"
            value={reportDate}
            onChange={onReportDateChange}
            error={reportDateErr}
          />
          <FormField
            label="Inspector"
            auto
            value={inspection?.inspectorName ?? inspectorName}
          />
        </div>
        <div className="craFieldRow cols3">
          <FormField label="Invoice Number" value={invoiceNum} auto />
          <FormField
            label="Invoice Amount"
            value={
              project?.settings?.constructionReportAuthoringFee
                ? "$" +
                  String(
                    project?.settings?.constructionReportAuthoringFee?.amount,
                  )
                : "0"
            }
            auto
          />
          <FormField
            label="Invoice Date"
            required
            type="date"
            value={invoiceDate}
            onChange={onInvoiceDateChange}
            error={invoiceDateErr}
          />
        </div>
      </Card>

      <Card
        title={`G702 Payment Application Summary${drawNum ? ` — Draw #${drawNum}` : ""}`}
        badge={{ label: "Auto-filled from Draw", variant: "auto" }}
      >
        <div className="aiaGrid">
          <div>
            {leftSummary.map(([num, label, val]) => (
              <div key={num} className="aiaRow">
                <span>
                  <span className="num">{num}</span>
                  <span>{label}</span>
                </span>
                <span>{val}</span>
              </div>
            ))}
          </div>
          <div>
            {rightSummary.map(([num, label, val, color, rowClass]) => (
              <div key={num} className={`aiaRow ${rowClass || ""}`}>
                <span>
                  <span className="num">{num}</span>
                  <span>{label}</span>
                </span>
                <span className={color || ""}>{val}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Step1ProjectDrawInfo;
